import { getAllowedDimsForMetric } from '@revelio/core';

import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import {
  type SecondaryDimensionValues,
  secondaryDimensionOptions,
} from '../../PageConfigProvider/types/dimension';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import { isSelectableChartType } from './is-selectable-chart-type';

// Primary dimension constraints - which dimensions are available per entity type
const PRIMARY_DIMENSION_TO_SEGMENT_MAPPING: Record<
  PrimaryFilterId,
  SecondaryDimensionValues[]
> = {
  [FilterId.COMPANY]: [
    'geography',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
    'timeseries',
  ],
  [FilterId.GEOGRAPHY]: [
    'rics',
    'company',
    'role',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
    'timeseries',
  ],
  [FilterId.ROLE]: [
    'rics',
    'company',
    'geography',
    'skills',
    'seniority',
    'education',
    'ethnicity',
    'gender',
    'timeseries',
  ],
};

// Metrics that support secondary dimensions (for filtering)
const SUPPORTED_METRICS: Record<MetricValues, boolean> = {
  headcount: true,
  growth_rate: true,
  hiring_rate: true,
  attrition_rate: true,
  tenure: true,
  salary: true,
  share_of_headcount: false,
  transitions_inflow: true,
  transitions_outflow: true,
  posting_salary: true,
  time_to_fill: true,
  postings_active: true,
  postings_new: true,
  postings_removed: true,
  postings_volume: true,
  total_compensation: true,
  base_salary: true,
  salary_growth: false,
  contractor_pay_rate: true,
  total_spend_salary: true,
  total_spend_compensation: true,
  base_salary_percentiles: true,
  total_compensation_percentiles: true,
  contractor_pay_rate_percentiles: true,
  overall: true,
  sentiment_top_bottom: true,
  business_outlook: true,
  career_growth: true,
  compensation: true,
  benefits: true,
  healthcare: true,
  retirement_pension: true,
  childcare: true,
  education_assistance: true,
  wellness_programs: true,
  work_life_balance: true,
  diversity_and_inclusion: true,
  leadership: true,
  culture: true,
  remote_work: true,
  onboarding: true,
  mentorship: true,
  technology: true,
  promotion_opportunities: true,
  paid_time_off: true,
  transparency: true,
  efficiency_and_bureaucracy: true,
  product_and_service_quality: true,
  prestige: true,
  coworkers: true,
  mission_and_vision: true,
  workplace_facilities: true,
  training: true,
  clients_and_customers: true,
  company_growth: true,
  schedule_flexibility: true,
  job_security: true,
  overtime: true,
  family_support: true,
  commission: true,
  independence: true,
  commute: true,
  safety: true,
  internal_communication: true,
  office_politics: true,
  harassment: true,
  honesty: true,
  ethics: true,
  commitment: true,
  compliance: true,
  integrity: true,
  positive_review: true,
  negative_review: true,
  screener_headcount: true,
  screener_growth: true,
  screener_growth_yoy: true,
  screener_hiring: true,
  screener_attrition: true,
  screener_salary: true,
  screener_tenure: true,
  screener_inflow: true,
  screener_outflow: true,
  td_users: false,
  entity_summary: false,
  demand_supply: false,
  demand_supply_percentile: false,
  wage_pressure: false,
  wage_pressure_percentile: false,
  market_tightness: false,
  market_share: false,
  target_companies_rec: false,
  time_to_fill_percentile: false,
  overview_map: false,
};

const isSupportedMetric = (
  metric: MetricValues | undefined
): metric is MetricValues => {
  if (!metric) return false;
  return SUPPORTED_METRICS[metric] === true;
};

type GetAvailableSegmentProps = {
  chartType?: ChartType;
  primaryFilterId: PrimaryFilterId;
  metric?: MetricValues;
};

export const getAvailableSegment = ({
  chartType,
  primaryFilterId,
  metric,
}: GetAvailableSegmentProps) => {
  if (!isSelectableChartType(chartType)) return [];
  if (chartType === 'bar') return [];
  if (!isSupportedMetric(metric)) return [];

  // Get allowed dimensions from the shared matrix
  const metricDims = getAllowedDimsForMetric(metric, chartType);

  // Get primary dimension constraints
  const primaryDims =
    PRIMARY_DIMENSION_TO_SEGMENT_MAPPING[primaryFilterId] ?? [];

  // Intersection: dimensions that are both allowed for metric+chartType AND available for primary dimension
  const segmentValues = metricDims.filter((dim) => primaryDims.includes(dim));

  if (
    (metric === 'total_spend_salary' ||
      metric === 'total_spend_compensation') &&
    primaryFilterId !== FilterId.COMPANY
  ) {
    return [];
  }

  return secondaryDimensionOptions.filter((option) =>
    segmentValues.includes(option.value)
  );
};

type GetAvailableSegmentsProps = Omit<GetAvailableSegmentProps, 'metric'> & {
  metrics?: MetricValues[];
};
export const getAvailableSegments = ({
  chartType,
  primaryFilterId,
  metrics,
}: GetAvailableSegmentsProps) => {
  const metricSegments =
    metrics?.map((metric) =>
      getAvailableSegment({ chartType, primaryFilterId, metric })
    ) ?? [];
  const metricSegmentsValues = metricSegments?.map((segment) =>
    segment.map((s) => s.value)
  );
  if (metricSegmentsValues.length === 0) return [];
  return secondaryDimensionOptions.filter((option) =>
    metricSegmentsValues.every((segments) => segments.includes(option.value))
  );
};
